import React, { useState, useEffect } from 'react';
import { useParams, Link } from 'react-router-dom';
import styled from 'styled-components';
import { 
  Card, 
  CardHeader, 
  CardBody, 
  Button, 
  OutlineButton, 
  Badge, 
  FlexContainer,
  Grid
} from '../../styles/StyledComponents';
import { 
  FaArrowLeft, 
  FaEdit, 
  FaDownload, 
  FaPrint, 
  FaEye, 
  FaFileAlt,
  FaCalendarAlt,
  FaUser,
  FaBuilding,
  FaTools,
  FaTruck,
  FaHandshake,
  FaCog,
  FaMoneyBillWave,
  FaReceipt,
  FaMapMarkerAlt,
  FaFileImage,
  FaFilePdf
} from 'react-icons/fa';
import IconWrapper from '../common/IconWrapper';
import { useAuth } from '../../context/AuthContext';
import apiService from '../../services/api';

const DetailContainer = styled.div`
  max-width: 900px;
  margin: 0 auto;
`;

const PageHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 2rem;
  padding: 1.5rem;
  background: linear-gradient(135deg, #ffffff 0%, #f8f9fa 100%);
  border-radius: 16px;
  box-shadow: 0 4px 20px rgba(0, 0, 0, 0.08);
  border: 1px solid #f1f3f4;
  
  @media (max-width: 768px) {
    flex-direction: column;
    align-items: flex-start;
    gap: 1rem;
    padding: 1rem;
  }
`;

const PageTitle = styled.h1`
  font-size: 2rem;
  font-weight: 700;
  color: #141340;
  margin: 0;
  display: flex;
  align-items: center;
  gap: 1rem;
  
  .title-icon {
    background: linear-gradient(135deg, #F68B21 0%, #e67c1a 100%);
    width: 48px;
    height: 48px;
    border-radius: 12px;
    display: flex;
    align-items: center;
    justify-content: center;
    color: white;
    font-size: 1.2rem;
    box-shadow: 0 8px 24px rgba(246, 139, 33, 0.3);
  }
  
  @media (max-width: 768px) {
    font-size: 1.75rem;
  }
`;

const ActionButton = styled(Button)`
  display: flex;
  align-items: center;
  gap: 0.5rem;
  padding: 0.75rem 1.5rem;
  font-size: 0.9rem;
  font-weight: 600;
  border-radius: 12px;
  box-shadow: 0 4px 15px rgba(246, 139, 33, 0.3);
  transition: all 0.3s ease;
  
  &:hover {
    transform: translateY(-2px);
    box-shadow: 0 6px 20px rgba(246, 139, 33, 0.4);
  }
`;

const BackNavigation = styled.div`
  margin-bottom: 1.5rem;
  padding: 1rem;
  background: linear-gradient(135deg, #ffffff 0%, #f8f9fa 100%);
  border-radius: 12px;
  box-shadow: 0 2px 12px rgba(20, 19, 64, 0.08);
  border: 1px solid #f1f3f4;
  
  .back-link {
    display: flex;
    align-items: center;
    gap: 0.5rem;
    color: #141340;
    text-decoration: none;
    font-weight: 600;
    font-size: 0.95rem;
    transition: all 0.3s ease;
    
    &:hover {
      color: #F68B21;
      transform: translateX(-2px);
    }
    
    .back-icon {
      font-size: 1.1rem;
    }
  }
`;

const DetailSection = styled.div`
  margin-bottom: 2rem;
`;

const SectionTitle = styled.h3`
  color: var(--secondary-color);
  border-bottom: 2px solid var(--light-gray);
  padding-bottom: 0.5rem;
  margin-bottom: 1rem;
  display: flex;
  align-items: center;
  gap: 0.5rem;
`;

const DetailItem = styled.div`
  margin-bottom: 1rem;
`;

const DetailLabel = styled.div`
  font-weight: 600;
  color: var(--dark-gray);
  margin-bottom: 0.25rem;
  display: flex;
  align-items: center;
  gap: 0.5rem;
`;

const DetailValue = styled.div`
  color: var(--text-color);
  font-size: 1.1rem;
`;

const ReceiptContainer = styled.div`
  margin-top: 1rem;
  padding: 1rem;
  background: var(--light-gray);
  border-radius: var(--border-radius);
  border: 2px dashed var(--border-color);
`;

const ReceiptImage = styled.img`
  max-width: 100%;
  max-height: 400px;
  border-radius: var(--border-radius);
  box-shadow: 0 4px 15px rgba(0, 0, 0, 0.1);
`;

const ReceiptActions = styled.div`
  display: flex;
  gap: 0.5rem;
  margin-top: 1rem;
`;

const StatusBadge = styled(Badge)`
  font-size: 0.9rem;
  padding: 0.35rem 0.75rem;
`;

const AmountDisplay = styled.div`
  font-size: 1.5rem;
  font-weight: 700;
  color: var(--primary-color);
  margin: 0.5rem 0;
`;

const ExpenseDetail: React.FC = () => {
  const { expenseId } = useParams<{ expenseId: string }>();
  const { user } = useAuth();
  const [expense, setExpense] = useState<any>(null);
  const [project, setProject] = useState<any>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    const fetchExpenseDetails = async () => {
      if (expenseId) {
        try {
          setLoading(true);
          setError(null);
          
          // Fetch expense details
          const expenseResponse = await apiService.getExpense(expenseId);
          const foundExpense = expenseResponse.data;
          
          if (foundExpense) {
            setExpense(foundExpense);
            
            // Fetch project details if projectId exists
            if (foundExpense.projectId) {
              try {
                const projectResponse = await apiService.getProject(foundExpense.projectId);
                if (projectResponse.success && projectResponse.data) {
                  setProject(projectResponse.data);
                }
              } catch (projectError) {
                console.error('Error fetching project:', projectError);
              }
            }
          } else {
            setError('Expense not found');
          }
        } catch (error: any) {
          console.error('Error fetching expense details:', error);
          setError('Failed to load expense details');
        } finally {
          setLoading(false);
        }
      }
    };

    fetchExpenseDetails();
  }, [expenseId]);

  const formatCurrency = (amount: number, currency: string = 'TZS') => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: currency,
      maximumFractionDigits: 0
    }).format(amount);
  };

  const formatDate = (date: Date) => {
    return new Date(date).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'long',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const getStatusVariant = (status: string) => {
    switch (status) {
      case 'Approved': return 'success';
      case 'Rejected': return 'danger';
      case 'Pending Approval': return 'warning';
      case 'Recorded': return 'info';
      default: return 'secondary';
    }
  };

  const getCategoryIcon = (category: string) => {
    switch (category) {
      case 'Materials': return FaBuilding;
      case 'Labor': return FaUser;
      case 'Equipment': return FaTools;
      case 'Transportation': return FaTruck;
      case 'Others': return FaCog;
      default: return FaMoneyBillWave;
    }
  };

  const getReceiptTypeIcon = (type: string) => {
    switch (type) {
      case 'TRA VAT': return FaFilePdf;
      case 'VAT + VRN': return FaFilePdf;
      case 'Informal': return FaFileAlt;
      case 'EFD VAT': return FaFilePdf;
      case 'EFD VAT Free': return FaFilePdf;
      default: return FaReceipt;
    }
  };

  if (loading) {
    return (
      <DetailContainer>
        <div style={{ textAlign: 'center', padding: '2rem' }}>
          <p>Loading expense details...</p>
        </div>
      </DetailContainer>
    );
  }

  if (error) {
    return (
      <DetailContainer>
        <div style={{ textAlign: 'center', padding: '2rem' }}>
          <p style={{ color: 'var(--danger-color)', marginBottom: '1rem' }}>{error}</p>
          <Button as={Link} to="/accounting">
            Back to Accounting
          </Button>
        </div>
      </DetailContainer>
    );
  }

  if (!expense) {
    return (
      <DetailContainer>
        <div style={{ textAlign: 'center', padding: '2rem' }}>
          <p>Expense not found</p>
          <Button as={Link} to="/accounting">
            Back to Accounting
          </Button>
        </div>
      </DetailContainer>
    );
  }

  return (
    <DetailContainer>
      <BackNavigation>
        <Link to="/accounting" className="back-link">
          <IconWrapper icon={FaArrowLeft} className="back-icon" />
          Back to Accounting
        </Link>
      </BackNavigation>
      
      <PageHeader>
        <PageTitle>
          <div className="title-icon">💰</div>
          Expense Details
        </PageTitle>
        <FlexContainer gap="0.5rem">
          <ActionButton as={Link} to={`/accounting/expense/edit/${expense._id || expense.id}`}>
            <IconWrapper icon={FaEdit} />
            Edit Expense
          </ActionButton>
          <OutlineButton>
            <IconWrapper icon={FaPrint} />
            Print
          </OutlineButton>
        </FlexContainer>
      </PageHeader>

      <Grid columns="2fr 1fr" gap="2rem">
        <div>
          <Card>
            <CardHeader>
              <FlexContainer justify="space-between" align="center">
                <SectionTitle>
                  <IconWrapper icon={FaMoneyBillWave} />
                  Expense Information
                </SectionTitle>
                <StatusBadge variant={getStatusVariant(expense.status)}>
                  {expense.status}
                </StatusBadge>
              </FlexContainer>
            </CardHeader>
            <CardBody>
              <Grid columns="1fr 1fr" gap="1rem">
                <DetailItem>
                  <DetailLabel>
                    <IconWrapper icon={getCategoryIcon(expense.category)} />
                    Category
                  </DetailLabel>
                  <DetailValue>
                    <Badge variant="primary">{expense.category}</Badge>
                  </DetailValue>
                </DetailItem>
                
                <DetailItem>
                  <DetailLabel>
                    <IconWrapper icon={FaMoneyBillWave} />
                    Amount
                  </DetailLabel>
                  <AmountDisplay>
                    {formatCurrency(expense.amount, expense.currency)}
                  </AmountDisplay>
                </DetailItem>
                
                <DetailItem>
                  <DetailLabel>
                    <IconWrapper icon={FaFileAlt} />
                    Description
                  </DetailLabel>
                  <DetailValue>{expense.description}</DetailValue>
                </DetailItem>
                
                <DetailItem>
                  <DetailLabel>
                    <IconWrapper icon={FaBuilding} />
                    Supplier
                  </DetailLabel>
                  <DetailValue>{expense.supplier}</DetailValue>
                </DetailItem>
                
                <DetailItem>
                  <DetailLabel>
                    <IconWrapper icon={FaReceipt} />
                    Receipt Number
                  </DetailLabel>
                  <DetailValue>{expense.receiptNumber}</DetailValue>
                </DetailItem>
                
                <DetailItem>
                  <DetailLabel>
                    <IconWrapper icon={getReceiptTypeIcon(expense.receiptType)} />
                    Receipt Type
                  </DetailLabel>
                  <DetailValue>
                    <Badge variant="info">{expense.receiptType}</Badge>
                  </DetailValue>
                </DetailItem>
                
                <DetailItem>
                  <DetailLabel>
                    <IconWrapper icon={FaCalendarAlt} />
                    Expense Date
                  </DetailLabel>
                  <DetailValue>{formatDate(new Date(expense.date))}</DetailValue>
                </DetailItem>
                
                <DetailItem>
                  <DetailLabel>
                    <IconWrapper icon={FaCalendarAlt} />
                    Recorded Date
                  </DetailLabel>
                  <DetailValue>{formatDate(new Date(expense.recordedAt))}</DetailValue>
                </DetailItem>
              </Grid>
            </CardBody>
          </Card>

          {expense.receiptImage && (
            <Card style={{ marginTop: '1rem' }}>
              <CardHeader>
                <SectionTitle>
                  <IconWrapper icon={FaFileImage} />
                  Receipt
                </SectionTitle>
              </CardHeader>
              <CardBody>
                <ReceiptContainer>
                  <ReceiptImage 
                    src={expense.receiptImage.url} 
                    alt="Receipt"
                    onError={(e) => {
                      e.currentTarget.style.display = 'none';
                      const nextElement = e.currentTarget.nextElementSibling as HTMLElement;
                      if (nextElement) {
                        nextElement.style.display = 'block';
                      }
                    }}
                  />
                  <div style={{ display: 'none', textAlign: 'center', padding: '2rem' }}>
                    <IconWrapper icon={FaFileImage} style={{ fontSize: '3rem', color: 'var(--dark-gray)' }} />
                    <p>Receipt image not available</p>
                  </div>
                  <ReceiptActions>
                    <Button>
                      <IconWrapper icon={FaDownload} />
                      Download
                    </Button>
                    <OutlineButton>
                      <IconWrapper icon={FaEye} />
                      View Full Size
                    </OutlineButton>
                  </ReceiptActions>
                </ReceiptContainer>
              </CardBody>
            </Card>
          )}
        </div>

        <div>
          <Card>
            <CardHeader>
              <SectionTitle>
                <IconWrapper icon={FaUser} />
                Recorded By
              </SectionTitle>
            </CardHeader>
            <CardBody>
              <DetailItem>
                <DetailLabel>Name</DetailLabel>
                <DetailValue>{expense.recordedBy?.name || 'Unknown'}</DetailValue>
              </DetailItem>
              <DetailItem>
                <DetailLabel>Email</DetailLabel>
                <DetailValue>{expense.recordedBy?.email || 'N/A'}</DetailValue>
              </DetailItem>
              <DetailItem>
                <DetailLabel>Role</DetailLabel>
                <DetailValue>{expense.recordedBy?.role || 'Cashier'}</DetailValue>
              </DetailItem>
              <DetailItem>
                <DetailLabel>Recorded At</DetailLabel>
                <DetailValue>{formatDate(new Date(expense.recordedAt))}</DetailValue>
              </DetailItem>
            </CardBody>
          </Card>

          {project && (
            <Card style={{ marginTop: '1rem' }}>
              <CardHeader>
                <SectionTitle>
                  <IconWrapper icon={FaBuilding} />
                  Project Information
                </SectionTitle>
              </CardHeader>
              <CardBody>
                <DetailItem>
                  <DetailLabel>Project Name</DetailLabel>
                  <DetailValue>{project.name}</DetailValue>
                </DetailItem>
                <DetailItem>
                  <DetailLabel>Project ID</DetailLabel>
                  <DetailValue>{project._id || project.id}</DetailValue>
                </DetailItem>
                <DetailItem>
                  <DetailLabel>Location</DetailLabel>
                  <DetailValue>{project.location}</DetailValue>
                </DetailItem>
                <DetailItem>
                  <DetailLabel>Status</DetailLabel>
                  <DetailValue>
                    <Badge variant="info">{project.status}</Badge>
                  </DetailValue>
                </DetailItem>
              </CardBody>
            </Card>
          )}

          {expense.approvedBy && (
            <Card style={{ marginTop: '1rem' }}>
              <CardHeader>
                <SectionTitle>
                  <IconWrapper icon={FaUser} />
                  Approved By
                </SectionTitle>
              </CardHeader>
              <CardBody>
                <DetailItem>
                  <DetailLabel>Name</DetailLabel>
                  <DetailValue>{expense.approvedBy?.name || 'Unknown'}</DetailValue>
                </DetailItem>
                <DetailItem>
                  <DetailLabel>Approved At</DetailLabel>
                  <DetailValue>{formatDate(new Date(expense.approvedAt))}</DetailValue>
                </DetailItem>
                {expense.approvalComments && (
                  <DetailItem>
                    <DetailLabel>Comments</DetailLabel>
                    <DetailValue>{expense.approvalComments}</DetailValue>
                  </DetailItem>
                )}
              </CardBody>
            </Card>
          )}
        </div>
      </Grid>
    </DetailContainer>
  );
};

export default ExpenseDetail; 